import React from "react";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import { faSortDown, faSortUp } from "@fortawesome/free-solid-svg-icons";

import {RoleBadge} from "../../../../designSystem/components/RoleBadge";
import {Badge} from "../../../../designSystem/components/Badge";

import {PERMISSION_LABEL_MAPPING} from "../../../../utils/constants";
import {UserOrganization} from "../../model/types";
import {ActionButton} from "../../../../designSystem/components/ActionButton";
import {DataCardLayout} from "../../../../designSystem/components/DataCardLayout";
import {Typography} from "../../../../designSystem/components/Typography";

interface UserTableProps {
  sortColumn: string;
  sortOrder: 'asc' | 'desc';
  users: UserOrganization[];
  handleSort: (sortColumn: string) => void;
  handleEdit: (user: UserOrganization) => void;
  handleDelete: (user: UserOrganization) => void;
  handleResetPassword: (user: UserOrganization) => void;
}

const PermissionBadgeList = ({ permissions }) => {
  if (!permissions) return null
  return (
    <div className="flex flex-row gap-2">
      {Object.keys(permissions).map((permission) => {
        if (permissions[permission]) {
          return (
            <Badge tone="gray" key={permission}>
              {PERMISSION_LABEL_MAPPING[permission]}
            </Badge>
          )
        }
      })}
    </div>
  )
}
export const UserTable = ({
  sortColumn,
  sortOrder,
  users,
  handleSort,
  handleEdit,
  handleDelete,
  handleResetPassword,
}: UserTableProps) => (
  <div>
    {/* Table view for md and above */}
    <div className="hidden md:block">
      <table className="w-full table-fixed border shadow-lg rounded-2xl overflow-hidden">
        <thead>
        <tr className="bg-white">
          <th
            className="p-2 border cursor-pointer"
            onClick={() => handleSort("name")}
          >
            姓名/名稱{" "}
            {sortColumn === "name" && <FontAwesomeIcon icon={sortOrder === "asc" ? faSortUp : faSortDown}/>}
          </th>
          <th
            className="p-2 border cursor-pointer w-[140px]"
            onClick={() => handleSort("role")}
          >
            貼心到宅角色{" "}
            {sortColumn === "role" && <FontAwesomeIcon icon={sortOrder === "asc" ? faSortUp : faSortDown}/>}
          </th>
          <th className="p-2 border cursor-pointer">
            權限
          </th>
          <th
            className="p-2 border cursor-pointer"
            onClick={() => handleSort("email")}
          >
            Email{" "}
            {sortColumn === "email" && <FontAwesomeIcon icon={sortOrder === "asc" ? faSortUp : faSortDown}/>}
          </th>
          <th className="p-2 border resize-x overflow-auto w-[320px]">
            動作
          </th>
        </tr>
        </thead>
        <tbody>
        {users.map(user => (
          <tr key={user.id} className="border bg-white">
            <td className="p-2 border overflow-x-auto whitespace-nowrap">
              {user.name}
            </td>
            <td className="p-2 border">
              <RoleBadge roleValue={user.role}/>
            </td>
            <td className="p-2 border max-w-[200px] overflow-x-auto whitespace-nowrap">
              <PermissionBadgeList permissions={user.permissions}/>
            </td>
            <td className="p-2 border overflow-x-auto whitespace-nowrap">
              {user.email}
            </td>
            <td className="pl-1 border">
              <ActionButton
                size="sm"
                variant="ghost"
                label="修改"
                iconName="fa-edit"
                onClick={() => handleEdit(user)}
              />
              <ActionButton
                size="sm"
                variant="ghost"
                label="刪除"
                iconName="fa-trash"
                color="warning"
                onClick={() => handleDelete(user)}
              />
              <ActionButton
                size="sm"
                variant="ghost"
                label="重設密碼"
                iconName="fa-redo"
                color="success"
                onClick={() => handleResetPassword(user)}
              />
            </td>
          </tr>
        ))}
        </tbody>
      </table>
    </div>

    {/* Card view for small screens */}
    <div className="md:hidden space-y-4">
      {users.map((user, index) => (
        <DataCardLayout
          key={`user${index}`}
          topRowSection={
            user.role ? <RoleBadge roleValue={user.role} /> : null
          }
          mainInfoSection={
            <React.Fragment>
              <Typography variant="title">
                {user.name}
              </Typography>

              <div className="flex flex-row gap-2 items-center">
                <Typography variant="bodyBold" className="text-nowrap">
                  屏衛權限:
                </Typography>
                <div className="w-full overflow-x-scroll">
                  <PermissionBadgeList permissions={user.permissions} />
                </div>
              </div>

              <div className="flex flex-row gap-2">
                <Typography variant="bodyBold">
                  Email:
                </Typography>
                <Typography variant="body">
                  {user.email}
                </Typography>
              </div>

              <div className="flex flex-row gap-2">
                <Typography variant="bodyBold">
                  機構:
                </Typography>
                <Typography variant="body">
                  {user.organization}
                </Typography>
              </div>
            </React.Fragment>
          }
          actionSection={
            <React.Fragment>
              <ActionButton
                variant="outline"
                label="修改"
                iconName="fa-edit"
                onClick={() => handleEdit(user)}
              />
              <ActionButton
                variant="outline"
                label="刪除"
                iconName="fa-trash"
                color="warning"
                onClick={() => handleDelete(user)}
              />
              <ActionButton
                variant="outline"
                label="重設密碼"
                iconName="fa-redo"
                color="success"
                onClick={() => handleResetPassword(user)}
              />
            </React.Fragment>
          }
        />
      ))}
    </div>
  </div>
);
